import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  Fade,
  useTheme,
  styled,
} from '@mui/material';
import _ from 'lodash';
import { useSnackbar } from 'notistack';

// Icons
import LockIcon from '@mui/icons-material/Lock';
import BusinessIcon from '@mui/icons-material/Business';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import EventIcon from '@mui/icons-material/Event';
import BarChartIcon from '@mui/icons-material/BarChart';
import PeopleIcon from '@mui/icons-material/People';
import HistoryIcon from '@mui/icons-material/History';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';

// Components
import AffiliateInfoRouter from '../AffiliateInfo/AffiliateInfoRouter';
import PrivateRoute from '../../components/PrivateRoute/PrivateRoute';
import CommitteeManagement from '../CommitteeManagement/CommitteeManagement';
import GroupRegistration from '../GroupRegistration/GroupRegistration';
import RegistrationRouter from '../../modules/RegistrationRouter/RegistrationRouter';
import Reporting from '../Reporting/Reporting';
import ConventionManagement from '../ConventionManagement/ConventionManagement';
import GuestInvitation from '../GuestInvitation/GuestInvitation';
import AuditAndHistoryRevisionsRouter from '../Audit/AuditAndHistoryRevisionsRouter';
import AccountManagement from '../../components/AccountManagement/AccountManagement';
import NotAuthorized from 'components/NotAuthorized/NotAuthorized';
import RolesManagement from '../RolesManagement/RolesManagement';
import Contact from 'modules/Contact/Contact';
import DelegateCertification from '../DelegateCertification/DelegateCerfification';
import UsersManagement from 'modules/UsersManagement/UsersManagement';
import GenericSpinner from 'components/GenericSpinner/GenericSpinner';
import Home from '../Home/Home';

// Assets
import logoBlue from '../../utils/images/AFT-Square-LogoBlue-TagWhite.png';

// Services and actions
import { callLogoutApi } from './services';
import {
  getAllConventions,
  setNewActiveConvention,
} from '../../actions/ConventionActions/ConventionActions';

// Store
import { useAppStore } from '../../stores';

// Permission enums
import { usertype_permissions } from '../../enums/PermissionEnums';

const drawerWidth = 240;
const headerHeight = 64;

// Styled components to replace makeStyles
// Use 'md' breakpoint (960px) so tablet portrait shows hamburger menu
const Main = styled('main')(({ theme }) => ({
  flexGrow: 1,
  minWidth: 0,
  padding: theme.spacing(3),
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  position: 'fixed',
  background: 'white',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  borderBottom: '1px solid #EAEAEA',
  // Header is to the RIGHT of the sidebar only on md and up (960px+)
  [theme.breakpoints.up('md')]: {
    width: `calc(100% - ${drawerWidth}px)`,
    marginLeft: drawerWidth,
  },
}));

// Nav wrapper to properly reserve space for the sidebar
const StyledDrawer = styled('nav')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    width: drawerWidth,
    flexShrink: 0,
  },
}));

interface ConventionType {
  conventionId: number;
  title: string;
}

interface UserRecordType {
  firstName?: string;
}

interface PermissionType {
  name: string;
}

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const displayMobile = useMediaQuery('(max-width:959px)');

  // Zustand store
  const userRecord = useAppStore((state) => state.user.user) as UserRecordType;
  const refreshToken = useAppStore((state) => state.user.refreshToken);
  const activeConvention = useAppStore((state) => state.conventionReducer.activeConvention) as ConventionType;
  const attendee = useAppStore((state) => state.attendee);
  const permissions = useAppStore((state) => state.user.permissions) as unknown as PermissionType[];
  const conventions = useAppStore((state) => state.conventionReducer.conventions) as ConventionType[];
  const resetStore = useAppStore((state) => state.resetStore);
  const setAllConventions = useAppStore((state) => state.setConventions);
  const setActiveConvention = useAppStore((state) => state.setActiveConvention);

  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutRequestPending, setLogoutRequestPending] = useState(false);
  const [conventionRequestPending, setConventionRequestPending] = useState(false);
  const [selectedConventionId, setSelectedConventionId] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [updateConventionRequestPending, setUpdateConventionRequestPending] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleClick = (link: string) => {
    navigate(link);
  };

  const checkPermissions = (module: string) => {
    const match = _.find(permissions, (p: PermissionType) => {
      return p.name.indexOf(module) >= 0;
    });
    return !!match;
  };

  const handleLogout = () => {
    setLogoutRequestPending(true);
    callLogoutApi(refreshToken)
      .then(() => {
        setLogoutRequestPending(false);
        window.sessionStorage.removeItem('loggedIn');
        resetStore();
        navigate('/landing');
      })
      .catch((error) => {
        enqueueSnackbar(
          'Logout error. Please close this tab to end your session',
          { variant: 'error' }
        );
        console.warn(error);
        setLogoutRequestPending(false);
      });
  };

  const isBackOfficeOrAdmin =
    _.find(permissions, (permission: PermissionType) => {
      return (
        permission.name.indexOf(usertype_permissions.BackOffice) !== -1 ||
        permission.name.indexOf(usertype_permissions.Admin) !== -1
      );
    }) !== undefined;

  const isStateFedOrAffiliatePresident =
    _.find(permissions, (permission: PermissionType) => {
      return (
        permission.name.indexOf(usertype_permissions.AffiliatePresident) !== -1 ||
        permission.name.indexOf(usertype_permissions.StateFed) !== -1
      );
    }) !== undefined;

  const isPresidentOrDesignee =
    _.find(permissions, (permission: PermissionType) => {
      return (
        permission.name.indexOf(usertype_permissions.AffiliatePresident) !== -1 ||
        permission.name.indexOf(usertype_permissions.Designee) !== -1 ||
        permission.name.indexOf(usertype_permissions.Admin) !== -1
      );
    }) !== undefined;

  const isPresidentOrDesigneeOrAdmin =
    _.find(permissions, (permission: PermissionType) => {
      return (
        permission.name.indexOf(usertype_permissions.AffiliatePresident) !== -1 ||
        permission.name.indexOf(usertype_permissions.Designee) !== -1 ||
        permission.name.indexOf(usertype_permissions.Admin) !== -1
      );
    }) !== undefined;

  const affiliateStaffAndLower =
    _.find(permissions, (permission: PermissionType) => {
      return (
        permission.name.indexOf(usertype_permissions.Member) !== -1 ||
        permission.name.indexOf(usertype_permissions.Visitor) !== -1 ||
        permission.name.indexOf(usertype_permissions.AffiliateStaff) !== -1
      );
    }) !== undefined;

  const isAffiliatePresident =
    _.find(permissions, (permission: PermissionType) => {
      return permission.name.indexOf(usertype_permissions.AffiliatePresident) !== -1;
    }) !== undefined;

  const renderDefaultRouteBasedOnRole = () => {
    if (location.pathname === '/' || location.pathname === '/portal') {
      let redirectPath = location.pathname;
      if (isAffiliatePresident) {
        redirectPath = '/home';
      } else if (isPresidentOrDesigneeOrAdmin) {
        redirectPath = '/home';
      } else if (isBackOfficeOrAdmin) {
        redirectPath = '/convention-registration';
      } else if (isStateFedOrAffiliatePresident) {
        redirectPath = '/home';
      } else if (affiliateStaffAndLower) {
        redirectPath = '/convention-registration/current-user';
      }
      return <Navigate to={redirectPath} replace />;
    }
    return null;
  };

  // Base modules list - will be filtered and reordered based on role
  const baseModules = [
    {
      text: 'Home',
      link: '/home',
      module: 'Home',
      component: Home,
      icon: <HomeIcon />,
      presidentDesigneeOnly: false,
    },
    {
      text: 'Delegate Certification',
      link: '/delegate-certification',
      module: 'DelegateCertification',
      component: DelegateCertification,
      icon: <HowToVoteIcon />,
    },
    {
      text: 'Registration',
      link: '/convention-registration',
      module: 'Registration',
      component: RegistrationRouter,
      icon: <HowToRegIcon />,
    },
    {
      text: 'Compliance',
      link: '/affiliate-info',
      module: 'AffiliateSummary',
      component: AffiliateInfoRouter,
      icon: <SearchIcon />,
      hideForPresidentDesignee: false,
    },
    {
      text: 'Manage Committees',
      link: '/manage-committees',
      module: 'CommitteeManagement',
      component: CommitteeManagement,
      icon: <BusinessIcon />,
    },
    {
      text: 'Role Management',
      link: '/roles-permissions',
      module: 'Permissions',
      component: RolesManagement,
      icon: <SupervisorAccountIcon />,
    },
    {
      text: 'Upload a List',
      link: '/group-registration',
      module: 'GroupRegistration',
      component: GroupRegistration,
      icon: <CloudUploadIcon />,
    },
    
    {
      text: 'Convention Management',
      link: '/convention-management',
      module: 'ConventionManagement',
      component: ConventionManagement,
      icon: <EventIcon />,
    },
    {
      text: 'Reporting',
      link: '/reporting',
      module: 'Reporting',
      component: Reporting,
      icon: <BarChartIcon />,
    },
    {
      text: 'Guest Invitations',
      link: '/guest-invitations',
      module: 'GuestInvite',
      component: GuestInvitation,
      icon: <PeopleIcon />,
    },
    {
      text: 'Audit/Revisions',
      link: '/audit-and-revisions',
      module: 'Audit',
      component: AuditAndHistoryRevisionsRouter,
      icon: <HistoryIcon />,
    },
    {
      text: 'Account Management',
      link: '/account-management',
      module: 'AccountManagement',
      component: AccountManagement,
      icon: <AccountCircleIcon />,
    },
    {
      text: 'Users Management',
      link: '/users-management',
      module: 'UserManagement',
      component: UsersManagement,
      icon: <PeopleIcon />,
    },
    {
      text: 'Contact Us',
      link: '/contact',
      module: 'Contact',
      component: Contact,
      icon: <ContactMailIcon />,
    }
  ];

  // Filter modules based on user role
  const modules = baseModules.filter((m) => {
    // Show President/Designee-only items only for those roles
    if (m.presidentDesigneeOnly && !isPresidentOrDesignee) {
      return false;
    }
    // Hide items marked as hidden for President/Designee
    if (m.hideForPresidentDesignee && isPresidentOrDesignee) {
      return false;
    }
    return true;
  });

  const checkForRouteToHideIfNotRegisteredForConvention = (routeLink: string) => {
    if (
      _.find(permissions, (permission: PermissionType) => {
        return (
          permission.name.indexOf(usertype_permissions.Member) !== -1 ||
          permission.name.indexOf(usertype_permissions.Visitor) !== -1 ||
          permission.name.indexOf(usertype_permissions.AffiliateStaff) !== -1
        );
      }) !== undefined
    ) {
      if (['/visitor-registration'].includes(routeLink)) {
        if (_.get(attendee, 'attendeeRecord.attendeeId') === 0) {
          return false;
        }
      }
    }
    return true;
  };

  // Load conventions for dropdown
  useEffect(() => {
    if (isBackOfficeOrAdmin && conventions.length === 0) {
      setConventionRequestPending(true);
      getAllConventions()
        .then((response: { data: ConventionType[] }) => {
          if (response && response.data) {
            setAllConventions(response.data);
          }
          setConventionRequestPending(false);
        })
        .catch((error: unknown) => {
          console.log(error);
          setConventionRequestPending(false);
        });
    }
  }, [isBackOfficeOrAdmin, conventions.length, setAllConventions]);

  const handleConventionButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    if (selectedConventionId !== 0) {
      setUpdateConventionRequestPending(true);
      const newConvention = _.find(conventions, {
        conventionId: selectedConventionId,
      });

      if (!newConvention) {
        setUpdateConventionRequestPending(false);
        enqueueSnackbar('Selected convention not found. Please try again.', {
          variant: 'error',
        });
        setSelectedConventionId(0);
        return;
      }

      setNewActiveConvention(newConvention)
        .then((response: { data: { convention: ConventionType; jwtToken: string } }) => {
          if (response && response.data && response.data.convention) {
            setActiveConvention(response.data.convention);
            setUpdateConventionRequestPending(false);
            enqueueSnackbar('Convention set!', { variant: 'success' });
            window.location.reload();
          } else {
            setUpdateConventionRequestPending(false);
            enqueueSnackbar('Failed to update convention. Invalid response from server.', {
              variant: 'error',
            });
          }
        })
        .catch((error: unknown) => {
          setUpdateConventionRequestPending(false);
          enqueueSnackbar('The following error occurred: ' + error, {
            variant: 'error',
          });
          console.log(error);
        });
    }
  }, [selectedConventionId, conventions, setActiveConvention, enqueueSnackbar]);

  const handleConventionButtonClose = (e: React.MouseEvent<HTMLElement>) => {
    const value = (e.target as HTMLElement).getAttribute('data-value');
    if (value !== null) {
      setSelectedConventionId(parseInt(value, 10));
    }
    setAnchorEl(null);
  };

  const renderCorrectValueForConventionDisplay = () => {
    if (isBackOfficeOrAdmin) {
      if (conventionRequestPending || updateConventionRequestPending) {
        return <GenericSpinner padding={0} includeOverlay={false} />;
      } else {
        return (
          <>
            <Button
              aria-controls="simple-menu"
              aria-haspopup="true"
              className="change-convention-button"
              onClick={handleConventionButtonClick}
              sx={{ minWidth: 0, p: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
            >
              {activeConvention?.title}
            </Button>
            <Menu
              id="convention-selection-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleConventionButtonClose}
              TransitionComponent={Fade}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              {conventions.map((convention: ConventionType, index: number) => {
                if (activeConvention?.title !== convention.title) {
                  return (
                    <MenuItem
                      key={index}
                      data-value={convention.conventionId}
                      className="convention-label"
                      onClick={handleConventionButtonClose}
                    >
                      {convention.title}
                    </MenuItem>
                  );
                }
                return null;
              })}
            </Menu>
          </>
        );
      }
    }
    return (
      <Typography noWrap className="portal-welcome-label text-right convention-text-truncate">
        <span className="welcome-user text-center p-3">
          {activeConvention?.title}
        </span>
      </Typography>
    );
  };

  const drawer = (
    <div className="navBar">
      <ListItem className="navBar-title">
        <ListItemText primary="Main Menu" />
      </ListItem>

      <List>
        {modules.map((obj) => {
          if (checkForRouteToHideIfNotRegisteredForConvention(obj.link)) {
            if (checkPermissions(obj.module) || obj.module === 'Contact' || (obj.module === 'Home' && (isPresidentOrDesigneeOrAdmin || isStateFedOrAffiliatePresident))) {
              return (
                <ListItemButton
                  className="nav-list-item-background"
                  key={obj.text}
                  onClick={() => handleClick(obj.link)}
                  selected={'/' + window.location.pathname.split('/')[1] === obj.link}
                  sx={{
                    cursor: 'pointer',
                    '&.Mui-selected': {
                      backgroundColor: '',
                      color: '#ffffff',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '40px' }}>{obj.icon}</ListItemIcon>
                  <ListItemText
                    sx={{ color: '#eeeeee' }}
                    primary={obj.text}
                  />
                </ListItemButton>
              );
            }
          }
          return null;
        })}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      {logoutRequestPending && (
        <GenericSpinner
          includeOverlay={true}
          overlayEntireModule={true}
          padding={25}
        />
      )}
      <StyledAppBar>
        {/* Mobile header - two rows (phones only, below 600px) */}
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          {/* Row 1: Hamburger | User | Logout */}
          <Toolbar sx={{ minHeight: `${headerHeight}px !important`, px: 1 }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ color: '#3A3A3A' }}
            >
              <MenuIcon />
            </IconButton>

            {/* Vertical divider */}
            <Box sx={{ height: '32px', borderLeft: '1px solid #E0E0E0', mx: 1 }} />

            {/* User name on mobile */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <PersonIcon className="user-icon" />
              <Typography noWrap className="welcome-user">
                {userRecord ? userRecord.firstName : 'User'}
              </Typography>
            </Box>

            {/* Logout button */}
            <Button onClick={handleLogout} className="logout-btn">
              <LockIcon className="logout-btn-icon" />
              Logout
            </Button>
          </Toolbar>

          {/* Row 2: Logo | Convention */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            borderTop: '1px solid #E0E0E0',
            borderBottom: '1px solid #E0E0E0',
            minHeight: '56px',
            backgroundColor: '#FFFFFF',
            px: 2
          }}>
            {/* Logo */}
            <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', overflow: 'hidden', width: '54px' }}>
              <img src={logoBlue} alt="aft-logo" style={{ height: '44px', width: 'auto', flexShrink: 0 }} />
            </Box>
            {/* Convention name - centered in remaining space */}
            <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', justifyContent: 'center', overflow: 'hidden' }}>
              {renderCorrectValueForConventionDisplay()}
            </Box>
          </Box>
        </Box>

        {/* Tablet/Desktop header - single row (600px and up) */}
        <Toolbar sx={{
          minHeight: `${headerHeight}px !important`,
          px: 2,
          display: { xs: 'none', sm: 'flex' }
        }}>
          {/* Hamburger menu - only show on tablet portrait (below md/960px) */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 1,
              display: { sm: 'flex', md: 'none' },
              color: '#3A3A3A',
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Vertical divider - only on tablet portrait */}
          <Box
            sx={{
              display: { sm: 'flex', md: 'none' },
              height: '32px',
              borderLeft: '1px solid #E0E0E0',
              mr: 1,
            }}
          />

          {/* Logo in header */}
          <Box sx={{ display: 'flex', alignItems: 'center', overflow: 'hidden', width: '67px' }}>
            <img src={logoBlue} alt="aft-logo" style={{ height: '56px', width: 'auto', flexShrink: 0 }} />
          </Box>

          {/* Convention title */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0, overflow: 'hidden' }}>
            {renderCorrectValueForConventionDisplay()}
          </Box>

          {/* User name section */}
          <Box sx={{ display: 'flex', alignItems: 'center', px: { sm: 1, md: 2 } }}>
            <PersonIcon className="user-icon" />
            <Typography noWrap className="welcome-user" sx={{ display: { sm: 'none', md: 'block' } }}>
              {userRecord ? userRecord.firstName : 'User'}
            </Typography>
          </Box>

          {/* Logout button */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button onClick={handleLogout} className="logout-btn">
              <LockIcon className="logout-btn-icon" />
              Logout
            </Button>
          </Box>
        </Toolbar>
      </StyledAppBar>
      <StyledDrawer aria-label="navigation sidebar">
        {/* Mobile/Tablet drawer - temporary, shown below md (960px) */}
        <Drawer
          variant="temporary"
          anchor={theme.direction === 'rtl' ? 'right' : 'left'}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#3A3A3A',
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer - permanent, shown at md (960px) and above */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#3A3A3A',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </StyledDrawer>
      <Main sx={{ marginTop: { xs: '120px', sm: `${headerHeight}px` } }}>
        {renderDefaultRouteBasedOnRole()}
        <Routes>
          {/* Use unique routes from baseModules to avoid duplicate route definitions */}
          {_.uniqBy(baseModules, 'link').map((m, index) => {
            if (checkForRouteToHideIfNotRegisteredForConvention(m.link)) {
              const ModuleComponent = m.component;
              return (
                <Route
                  key={index}
                  path={`${m.link}/*`}
                  element={
                    <PrivateRoute module={m.module}>
                      <ModuleComponent />
                    </PrivateRoute>
                  }
                />
              );
            }
            return null;
          })}
          <Route
            path="/not-authorized"
            element={<NotAuthorized />}
          />
        </Routes>
      </Main>
    </Box>
  );
};

export default Dashboard;
